import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR, { mutate as globalMutate } from 'swr';
import { useAuth } from '@/auth/AuthContext';
import { me, webhooks, type SubscriptionUpsert, type TenantSelf } from '@/api/tenant';
import { DataTable } from '@/components/DataTable';
import { FieldRow } from '@/components/FieldRow';
import { MetricStat } from '@/components/MetricStat';
import { Pagination } from '@/components/Pagination';
import { StatusPill, webhookStatusTone } from '@/components/StatusPill';
import { useToast } from '@/components/Toaster';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime, utcStamp } from '@/utils/format';
import type { WebhookOutbox, WebhookOutboxStatus, WebhookSubscription } from '@/types/backend';

/**
 * Tenant view of recent webhook deliveries + registered subscriptions.
 *
 * Backed by `/api/abdm/webhooks/*` — the connector auto-scopes every
 * read and write to the caller's tenant via X-Tenant-Id, so there's no
 * cross-tenant exposure even if the JWT is mishandled.
 *
 * Tabs:
 *   • Deliveries     — paginated outbound deliveries with replay-from-DLQ.
 *   • Subscriptions  — CRUD over the tenant's registered receivers.
 */

const PAGE_SIZE = 50;
type TabKey = 'deliveries' | 'subscriptions';

/**
 * Fallback event-type list when /me/tenant hasn't loaded yet. Once the
 * tenant-self response arrives, we use {@code tenant.allowedEventTypes}
 * (which is api-access-aware) instead.
 */
const FALLBACK_EVENT_TYPES = [
  'abha.verified',
  'abha.created',
  'care-context.discovered',
  'care-context.linked',
  'consent.granted',
  'consent.revoked',
  'data.fetched',
];

export function MyWebhooksPage() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') ?? 'deliveries') as TabKey;
  const [picked, setPicked] = useState<WebhookOutbox | null>(null);

  const { data: tenant, error: tenantError } = useSWR('me-tenant', () => me.tenant());

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const refreshAll = () => globalMutate((k) => {
    if (k === 'my-wh-subscriptions' || k === 'me-tenant') return true;
    return Array.isArray(k) && (k[0] === 'my-wh-deliveries' || k[0] === 'my-wh-count');
  });

  return (
    <PageShell
      kicker="TENANT · WEBHOOKS"
      title="My Webhooks"
      subtitle="Outbound deliveries and registered receivers for your tenant."
      width="full"
      actions={
        <IconButton onClick={refreshAll} aria-label="refresh"><RefreshIcon fontSize="small" /></IconButton>
      }
    >
      {tenant && <CallbackSummary tenant={tenant} />}
      {tenantError && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: theme.radius.sm + 'px' }}>
          Couldn&apos;t load your tenant info — falling back to the full event list.
        </Alert>
      )}

      <DeliveryStats />

      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => updateParam('tab', v)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          mt: 3,
          minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 500 },
        }}
      >
        <Tab value="deliveries" label="Deliveries" />
        <Tab value="subscriptions" label="Subscriptions" />
      </Tabs>

      {tab === 'deliveries' ? (
        <DeliveriesTab
          statusFilter={(searchParams.get('status') ?? '') as WebhookOutboxStatus | ''}
          page={Number(searchParams.get('page') ?? '0')}
          onParam={updateParam}
          onPick={(row) => setPicked(row)}
        />
      ) : (
        <SubscriptionsTab tenant={tenant ?? null} />
      )}

      <DeliveryDrawer pick={picked} onClose={() => setPicked(null)} onReplayed={refreshAll} />
    </PageShell>
  );
}

// ===================================================================
// Callback summary — explains where ABDM sends gateway callbacks
// ===================================================================

function CallbackSummary({ tenant }: { tenant: TenantSelf }) {
  const theme = useTheme();
  const isPlatform = tenant.integrationMode === 'PLATFORM';
  const isSdk = tenant.integrationMode === 'SDK';

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: theme.radius.sm + 'px',
        border: `1px solid ${theme.palette.border.subtle}`,
        bgcolor: theme.palette.surface.sunken,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Stack spacing={0.25} sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="overline" color="text.tertiary">L1 ABDM CALLBACK</Typography>
            <Chip
              size="small"
              label={tenant.integrationMode}
              color={isPlatform ? 'default' : isSdk ? 'info' : 'success'}
              variant="outlined"
            />
          </Stack>
          {isPlatform ? (
            <>
              <Typography variant="body2">
                You&apos;re on <strong>PLATFORM</strong> mode. ABDM gateway callbacks land on the platform&apos;s shared receiver,
                we route them to your tenant. You don&apos;t need to register a per-tenant L1 callback URL.
              </Typography>
              {tenant.platformCallbackUrl && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Platform L1 URL: {tenant.platformCallbackUrl}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2">
                You bring your own ABDM L1 client_id + secret. Register the URL below with ABDM as your gateway-callback target.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Your L1 URL: {tenant.callbackUrl ?? '— not set; contact platform admin to provision'}
              </Typography>
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

function DeliveryStats() {
  const { claims } = useAuth();
  const tenantId = claims?.tenantId;
  const fetchCount = (status: WebhookOutboxStatus) =>
    webhooks.deliveries({ status, page: 0, size: 1 }).then((p) => p.totalElements).catch(() => null);

  const { data: pending } = useSWR(['my-wh-count', tenantId, 'PENDING'], () => fetchCount('PENDING'));
  const { data: inFlight } = useSWR(['my-wh-count', tenantId, 'IN_FLIGHT'], () => fetchCount('IN_FLIGHT'));
  const { data: dlq } = useSWR(['my-wh-count', tenantId, 'DLQ'], () => fetchCount('DLQ'));
  const { data: sent } = useSWR(['my-wh-count', tenantId, 'SENT'], () => fetchCount('SENT'));

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
      <MetricStat label="Pending"   value={pending  ?? '…'} tone="info"    hint="awaiting dispatcher" />
      <MetricStat label="In flight" value={inFlight ?? '…'} tone="warning" hint="POST in progress" />
      <MetricStat label="Sent"      value={sent     ?? '…'} tone="success" hint="2xx confirmed" />
      <MetricStat label="DLQ"       value={dlq      ?? '…'} tone={(dlq ?? 0) > 0 ? 'danger' : 'success'} hint={(dlq ?? 0) > 0 ? 'needs replay' : 'clean'} />
    </Box>
  );
}

function DeliveriesTab({
  statusFilter,
  page,
  onParam,
  onPick,
}: {
  statusFilter: WebhookOutboxStatus | '';
  page: number;
  onParam: (k: string, v: string) => void;
  onPick: (row: WebhookOutbox) => void;
}) {
  const theme = useTheme();
  const query = useMemo(() => ({
    status: statusFilter || undefined,
    page,
    size: PAGE_SIZE,
  }), [statusFilter, page]);
  const swrKey = useMemo(() => ['my-wh-deliveries', query] as const, [query]);
  const { data, error, isLoading } = useSWR(swrKey, () => webhooks.deliveries(query));

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Status"
          select
          value={statusFilter}
          onChange={(e) => onParam('status', e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="PENDING">PENDING</MenuItem>
          <MenuItem value="IN_FLIGHT">IN_FLIGHT</MenuItem>
          <MenuItem value="SENT">SENT</MenuItem>
          <MenuItem value="DLQ">DLQ</MenuItem>
        </TextField>
      </Stack>

      <DataTable<WebhookOutbox>
        rows={data?.content}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{ icon: WebhookIcon, title: 'No deliveries yet.', description: 'When your registered receiver receives an event, it appears here.' }}
        onRowClick={onPick}
        columns={[
          {
            header: 'When',
            cell: (r) => <Typography variant="caption" color="text.secondary" title={utcStamp(r.createdAt)}>{relativeTime(r.createdAt)}</Typography>,
            width: 130,
          },
          {
            header: 'Event',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={500}>{r.eventType}</Typography>
                <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: theme.typography.fontFamily }}>
                  {r.url}
                </Typography>
              </Stack>
            ),
          },
          {
            header: 'Status',
            cell: (r) => <StatusPill tone={webhookStatusTone(r.status)}>{r.status}</StatusPill>,
            width: 110,
          },
          {
            header: 'Attempts',
            align: 'right',
            cell: (r) => <Typography variant="caption">{r.attempts}/{r.maxAttempts}</Typography>,
            width: 100,
          },
          {
            header: 'Last response',
            cell: (r) => r.lastResponseStatus ? (
              <Typography variant="caption">{r.lastResponseStatus}</Typography>
            ) : <Typography variant="caption" color="text.tertiary">—</Typography>,
            width: 120,
          },
        ]}
      />
      <Pagination page={data} onChange={(n) => onParam('page', String(n))} />
    </>
  );
}

function SubscriptionsTab({ tenant }: { tenant: TenantSelf | null }) {
  const theme = useTheme();
  const toast = useToast();
  const { data, error, isLoading } = useSWR('my-wh-subscriptions', () => webhooks.subscriptions());

  const [editing, setEditing] = useState<WebhookSubscription | null>(null);
  const [creating, setCreating] = useState(false);
  const [secretReveal, setSecretReveal] = useState<{ url: string; secret: string } | null>(null);

  const refresh = () => globalMutate('my-wh-subscriptions');

  const onDelete = async (sub: WebhookSubscription) => {
    if (!window.confirm(`Delete subscription for ${sub.url}?`)) return;
    try {
      await webhooks.deleteSubscription(sub.id);
      toast.success('Subscription deleted', sub.url);
      refresh();
    } catch (e) {
      toast.error('Could not delete', (e as Error).message);
    }
  };

  // PLATFORM-mode tenants don't manage their own webhook receiver —
  // they ride the shared platform L1 callback. Show a clear banner
  // instead of the CRUD UI.
  if (tenant && tenant.integrationMode === 'PLATFORM') {
    return (
      <Alert severity="info" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
        <Typography variant="body2" fontWeight={500}>
          Platform mode — webhook subscriptions are managed for you.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Your tenant rides the platform&apos;s shared L1 callback URL. ABDM events land at the platform receiver and we
          route them to your HMIS via the platform&apos;s standard channels. To register your own webhook URL,
          ask the platform admin to switch your integration mode to <strong>ENTERPRISE</strong> or <strong>SDK</strong>.
        </Typography>
      </Alert>
    );
  }

  // Restrict the offered event types to the API access matrix.
  // Falls back to the full known set until /me/tenant resolves so the
  // dialog isn't broken on the first paint.
  const allowedEventTypes = tenant?.allowedEventTypes && tenant.allowedEventTypes.length > 0
    ? tenant.allowedEventTypes
    : FALLBACK_EVENT_TYPES;

  return (
    <>
      {tenant && <PlanApiSummary tenant={tenant} />}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, mt: 2 }}>
        <Typography variant="overline" color="text.tertiary">YOUR REGISTERED RECEIVERS</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          New Subscription
        </Button>
      </Stack>

      <DataTable<WebhookSubscription>
        rows={data}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{
          icon: WebhookIcon,
          title: 'No subscriptions registered.',
          description: 'Register your HMIS receiver URL — incoming ABDM events will be POSTed there with an HMAC signature.',
          action: <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>New Subscription</Button>,
        }}
        columns={[
          {
            header: 'URL',
            cell: (r) => (
              <Typography variant="caption" sx={{ fontFamily: theme.typography.fontFamily, wordBreak: 'break-all' }}>
                {r.url}
              </Typography>
            ),
          },
          {
            header: 'Events',
            cell: (r) => r.eventTypes && r.eventTypes.length > 0 ? (
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {r.eventTypes.slice(0, 3).map((t) => <Chip key={t} label={t} size="small" variant="outlined" />)}
                {r.eventTypes.length > 3 && (
                  <Tooltip title={r.eventTypes.slice(3).join(', ')}>
                    <Chip label={`+${r.eventTypes.length - 3}`} size="small" />
                  </Tooltip>
                )}
              </Stack>
            ) : <Typography variant="caption" color="text.secondary">all events</Typography>,
          },
          {
            header: 'Active',
            cell: (r) => r.active ? <StatusPill tone="success">Yes</StatusPill> : <StatusPill tone="mono">No</StatusPill>,
            width: 100,
          },
          {
            header: 'Secret KEK',
            cell: (r) => (
              <Typography variant="caption" sx={{ fontFamily: theme.typography.fontFamily, color: r.secretId === 'plaintext' ? theme.palette.status.warning : 'text.secondary' }}>
                {r.secretId ?? '—'}
              </Typography>
            ),
            width: 140,
          },
          {
            header: '',
            cell: (r) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </Stack>
            ),
            width: 100,
            align: 'right',
          },
        ]}
      />

      <SubscriptionDialog
        open={creating || !!editing}
        existing={editing}
        availableEventTypes={allowedEventTypes}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={(saved, plaintextSecret) => {
          refresh();
          if (plaintextSecret) {
            setSecretReveal({ url: saved.url, secret: plaintextSecret });
          }
        }}
      />

      <SecretRevealDialog reveal={secretReveal} onClose={() => setSecretReveal(null)} />
    </>
  );
}

// ===================================================================
// Plan summary — what APIs are included in this tenant's tier
// ===================================================================

function PlanApiSummary({ tenant }: { tenant: TenantSelf }) {
  const theme = useTheme();
  // Group APIs by milestone for display.
  const grouped: Record<string, TenantSelf['allowedApis']> = {};
  for (const api of tenant.allowedApis) {
    if (!grouped[api.milestone]) grouped[api.milestone] = [];
    grouped[api.milestone]!.push(api);
  }
  const orderedMilestones = ['AUTH', 'M1', 'M2', 'M3', 'TENANT', 'ADMIN']
    .filter((m) => grouped[m] && grouped[m]!.length > 0);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: theme.radius.sm + 'px',
        border: `1px solid ${theme.palette.border.subtle}`,
        bgcolor: theme.palette.surface.canvas,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.tertiary">YOUR PLAN</Typography>
        <Chip size="small" label={tenant.plan ?? 'STARTER'} color="primary" variant="outlined" />
        <Chip size="small" label={tenant.planMilestones.filter((m) => m !== 'AUTH').join(' + ') || 'M1'} variant="outlined" />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        APIs included in your subscription tier — the events they publish are the ones you can subscribe to below.
      </Typography>
      <Stack spacing={1.5}>
        {orderedMilestones.map((m) => (
          <Box key={m}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
              {m}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {grouped[m]!.map((api) => (
                <Tooltip key={api.apiCode} title={`${api.httpMethod ?? ''} ${api.pathPattern ?? ''}`} arrow>
                  <Chip
                    size="small"
                    label={api.name}
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Box>
        ))}
        {orderedMilestones.length === 0 && (
          <Typography variant="caption" color="text.tertiary">
            No APIs are currently active for your plan. Contact the platform admin.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

// ===================================================================
// Create / Edit subscription dialog
// ===================================================================

function SubscriptionDialog({
  open,
  existing,
  availableEventTypes,
  onClose,
  onSaved,
}: {
  open: boolean;
  existing: WebhookSubscription | null;
  availableEventTypes: string[];
  onClose: () => void;
  onSaved: (s: WebhookSubscription, plaintextSecret?: string) => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const isEdit = !!existing;

  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [secretId, setSecretId] = useState('');
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [active, setActive] = useState(true);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUrl(existing?.url ?? '');
    setSecret('');
    setSecretId(existing?.secretId ?? '');
    setEventTypes(existing?.eventTypes ?? []);
    setActive(existing?.active ?? true);
    setDescription(existing?.description ?? '');
    setSubmitting(false);
  }, [open, existing]);

  const onSubmit = async () => {
    if (!url.trim()) { toast.error('URL required'); return; }
    if (!isEdit && !secret.trim()) { toast.error('Secret required for new subscriptions'); return; }
    setSubmitting(true);
    try {
      const payload: Partial<SubscriptionUpsert> = {
        url: url.trim(),
        eventTypes,
        active,
        description: description.trim() || undefined,
      };
      if (secret.trim()) {
        payload.secret = secret.trim();
        if (secretId.trim()) payload.secretId = secretId.trim();
      }
      const saved = isEdit
        ? await webhooks.updateSubscription(existing!.id, payload)
        : await webhooks.createSubscription(payload as SubscriptionUpsert);
      toast.success(isEdit ? 'Subscription updated' : 'Subscription created', saved.url);
      onSaved(saved, secret.trim() || undefined);
      onClose();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error ?? (e as Error).message;
      toast.error('Save failed', msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {isEdit ? 'Edit subscription' : 'New webhook subscription'}
        <Typography variant="caption" color="text.tertiary" sx={{ display: 'block' }}>
          ABDM events POSTed to your URL with an HMAC signature header.
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Receiver URL"
            placeholder="https://hmis.example.com/abdm/webhooks"
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            helperText="Public HTTPS endpoint that accepts POST + JSON. Must reply 2xx within 10 s."
            autoFocus
          />
          <TextField
            label={isEdit ? 'Rotate secret (leave blank to keep current)' : 'Shared secret'}
            placeholder="32+ chars of high-entropy text"
            fullWidth
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            helperText="Used to HMAC-SHA256 every delivery body. We display it once on save — store it on your side."
          />
          <TextField
            label="Secret ID (optional)"
            placeholder="auto-generated if blank"
            fullWidth
            value={secretId}
            onChange={(e) => setSecretId(e.target.value)}
            helperText="Stable identifier sent in X-Webhook-Signature so your verifier can pick the right key during rotations."
          />
          <FormControl fullWidth>
            <InputLabel>Event types</InputLabel>
            <Select
              multiple
              value={eventTypes}
              onChange={(e) => setEventTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label="Event types" />}
              renderValue={(selected) => selected.length === 0 ? 'all events' : (selected as string[]).join(', ')}
            >
              {availableEventTypes.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.tertiary" sx={{ mt: 0.5, ml: 1.5 }}>
              Empty = receive everything we emit. Pick a subset to filter at the source.
            </Typography>
          </FormControl>
          <TextField
            label="Description"
            placeholder="What's this receiver for?"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FormControlLabel
            control={<Switch checked={active} onChange={(_, v) => setActive(v)} />}
            label={active ? 'Active — deliveries will fire' : 'Inactive — deliveries paused'}
          />
        </Stack>
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, borderTop: `1px solid ${theme.palette.border.subtle}` }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={onSubmit} disabled={submitting}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </Box>
    </Dialog>
  );
}

function SecretRevealDialog({ reveal, onClose }: { reveal: { url: string; secret: string } | null; onClose: () => void }) {
  const theme = useTheme();
  const toast = useToast();
  if (!reveal) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reveal.secret);
      toast.success('Secret copied');
    } catch {
      toast.error('Copy failed', 'Select the value and copy manually.');
    }
  };

  return (
    <Dialog open={!!reveal} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        Save this secret
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
            We won&apos;t show this again. Configure your receiver at <code>{reveal.url}</code> to verify HMAC-SHA256 signatures
            with this exact value.
          </Alert>
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.surface.sunken,
              borderRadius: theme.radius.sm + 'px',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 13,
              wordBreak: 'break-all',
            }}
          >
            {reveal.secret}
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="contained" startIcon={<CopyIcon />} onClick={copy}>Copy secret</Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ===================================================================
// Delivery drawer (read + replay)
// ===================================================================

function DeliveryDrawer({ pick, onClose, onReplayed }: { pick: WebhookOutbox | null; onClose: () => void; onReplayed: () => void }) {
  const theme = useTheme();
  const toast = useToast();
  const [replaying, setReplaying] = useState(false);

  const onReplay = async () => {
    if (!pick) return;
    setReplaying(true);
    try {
      await webhooks.replay(pick.id);
      toast.success('Replay queued', `Next attempt scheduled.`);
      onReplayed();
      onClose();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error ?? (e as Error).message;
      toast.error('Replay failed', msg);
    } finally {
      setReplaying(false);
    }
  };

  return (
    <Drawer anchor="right" open={!!pick} onClose={onClose} PaperProps={{ sx: { width: { xs: '100vw', md: 600 } } }}>
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.border.subtle}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.tertiary">DELIVERY</Typography>
            <Typography variant="h2" sx={{ fontSize: 18, fontWeight: 600 }}>{pick?.eventType ?? ''}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {pick?.status === 'DLQ' && (
              <Button size="small" variant="contained" startIcon={<ReplayIcon />} onClick={onReplay} disabled={replaying}>
                Replay
              </Button>
            )}
            <IconButton onClick={onClose} aria-label="close drawer"><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ p: 3, overflowY: 'auto' }}>
        {pick && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>State</Typography>
              <FieldRow label="Status" value={<StatusPill tone={webhookStatusTone(pick.status)}>{pick.status}</StatusPill>} />
              <FieldRow label="Attempts" value={`${pick.attempts} of ${pick.maxAttempts}`} />
              <FieldRow label="Created" value={utcStamp(pick.createdAt)} />
              <FieldRow label="Last attempt" value={pick.lastAttemptAt ? utcStamp(pick.lastAttemptAt) : '—'} />
              <FieldRow label="Delivered" value={pick.deliveredAt ? utcStamp(pick.deliveredAt) : '—'} />
              <FieldRow label="Next retry" value={pick.nextRetryAt ? utcStamp(pick.nextRetryAt) : '—'} />
              <FieldRow label="Last response" value={pick.lastResponseStatus ?? '—'} />
              {pick.lastError && <FieldRow label="Last error" value={pick.lastError} />}
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>Routing</Typography>
              <FieldRow label="Subscription" value={pick.subscriptionId} mono />
              <FieldRow label="URL" value={pick.url} mono />
              <FieldRow label="Idempotency" value={redactMiddle(pick.idempotencyKey, 6, 6)} mono />
              <FieldRow label="Trace" value={pick.traceId ?? '—'} mono />
              <FieldRow label="Secret KEK" value={pick.secretId ?? '—'} mono />
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>Payload</Typography>
              <Box
                component="pre"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 11,
                  bgcolor: theme.palette.surface.sunken,
                  borderRadius: theme.radius.sm + 'px',
                  p: 2,
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(pick.payloadJson), null, 2);
                  } catch {
                    return pick.payloadJson;
                  }
                })()}
              </Box>
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
